import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { effectiveCardValue, MAX_CARD_LEVEL, scaleCombatStats, type CardCustomization } from '@anidex/shared/economy';
import type { CharacterDetailDTO } from '@anidex/shared/types';
import { RankBadge, RarityBadge } from '../../components/Badges';
import { AnimaAmount } from '../../components/Brand';
import { CharacterCard } from '../../components/CharacterCard';
import { HeartIcon, MarketIcon, ProfileIcon, ShareIcon, StarIcon } from '../../components/Icons';
import { NametagInput } from '../../components/NametagInput';
import { Sheet } from '../../components/Sheet';
import { ErrorState, PageLoader } from '../../components/States';
import { useToasts } from '../../components/Toasts';
import { useCardSheet } from '../../hooks/useCardSheet';
import { upgradeUnlock } from '../../i18n/catalog';
import { errorMessage } from '../../services/api';
import { formatDate, formatNumber, rarityLabel, roleLabel } from '../../services/format';
import { useCardNametags, useCharacter, useSetAvatar, useSetCardNametag, useToggleFavorite, useToggleWish, useUpgrade } from '../../services/queries';
import { ShareCardSheet } from './ShareCardSheet';
import { SellSheet } from '../marketplace/SellSheet';
import { CardWorkshop } from './CardWorkshop';

export function CardDetailSheet() {
  const { t } = useTranslation('collection');
  const { openId, close } = useCardSheet();
  const query = useCharacter(openId);

  return (
    <Sheet open={openId !== null} onClose={close} title={query.data?.character.name ?? t('detail.fallbackTitle')} wide>
      {query.isPending ? (
        <PageLoader />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <CardDetail key={query.data.character.id} detail={query.data} />
      )}
    </Sheet>
  );
}

function CardNametagEditor({ characterId, nametag }: { characterId: number; nametag: string | null }) {
  const { t } = useTranslation('collection');
  const toasts = useToasts();
  const [draft, setDraft] = useState(nametag ?? '');
  const [saved, setSaved] = useState(nametag);
  const updateNametag = useSetCardNametag();
  const normalized = draft.trim();
  const [suggestionPrefix, setSuggestionPrefix] = useState('');
  const suggestions = useCardNametags(suggestionPrefix);
  useEffect(() => {
    const timer = setTimeout(() => setSuggestionPrefix(normalized), 300);
    return () => clearTimeout(timer);
  }, [normalized]);
  const value = normalized || null;
  const unchanged = value === saved;

  return (
    <form
      className="card-nametag-editor"
      onSubmit={(event) => {
        event.preventDefault();
        updateNametag.mutate(
          { characterId, nametag: value },
          {
            onSuccess: (result) => {
              setSaved(result.nametag);
              setDraft(result.nametag ?? '');
              toasts.push({ tone: 'success', title: t('detail.nametagSaved') });
            },
            onError: (error) =>
              toasts.push({ tone: 'error', title: t('detail.nametagFailed'), description: errorMessage(error) }),
          },
        );
      }}
    >
      <div className="card-nametag-editor__row">
        <div className="field">
          <label className="field__label" htmlFor={`card-nametag-${characterId}`}>{t('detail.nametag')}</label>
          <NametagInput
            id={`card-nametag-${characterId}`}
            value={draft}
            onChange={setDraft}
            placeholder={t('detail.nametagPlaceholder')}
            suggestions={suggestions.data?.items ?? []}
          />
        </div>
        <button type="submit" className="button" disabled={unchanged || updateNametag.isPending}>
          {t('detail.saveNametag')}
        </button>
      </div>
      <p className="hint">{t('detail.nametagHint')}</p>
      {suggestions.isError ? <ErrorState error={suggestions.error} onRetry={() => void suggestions.refetch()} /> : null}
    </form>
  );
}

function CardDetail({ detail }: { detail: CharacterDetailDTO }) {
  const { t } = useTranslation('collection');
  const { character, ownership } = detail;
  const upgrade = useUpgrade();
  const favorite = useToggleFavorite();
  const wish = useToggleWish();
  const avatar = useSetAvatar();
  const toasts = useToasts();
  const [shareOpen, setShareOpen] = useState(false);
  const [sellOpen, setSellOpen] = useState(false);
  const [draftCustomization, setDraftCustomization] = useState<CardCustomization | null>(null);
  const [previewLevel, setPreviewLevel] = useState<number | null>(null);
  const [unlockedLevel, setUnlockedLevel] = useState<number | null>(null);
  const [workshopTab, setWorkshopTab] = useState<'evolution' | 'customization'>('evolution');
  const customization = draftCustomization ?? ownership?.customization ?? {};
  const step = ownership?.nextUpgrade ?? null;
  const role = roleLabel(character.role);

  useEffect(() => {
    if (unlockedLevel === null) return;
    const timer = setTimeout(() => setUnlockedLevel(null), 1600);
    return () => clearTimeout(timer);
  }, [unlockedLevel]);

  const runUpgrade = () =>
    upgrade.mutate(character.id, {
      onSuccess: (result) => {
        setPreviewLevel(null);
        setUnlockedLevel(result.ownership.upgradeLevel);
        toasts.push({
          tone: 'success',
          title: t('detail.upgraded', { level: result.ownership.upgradeLevel }),
          description: t('detail.unlocked', {
            unlock: upgradeUnlock(result.ownership.upgradeLevel, result.unlocked),
          }),
        });
      },
      onError: (error) => toasts.push({ tone: 'error', title: t('detail.upgradeFailed'), description: errorMessage(error) }),
    });

  return (
    <div className={`card-detail${workshopTab === 'customization' ? ' card-detail--customizing' : ''}${previewLevel !== null ? ' card-detail--previewing' : ''}`}>
      <div className={`card-detail__stage scene night rarity-${character.rarity}`}>
        <CharacterCard
          character={character}
          ownership={ownership ? { ...ownership, upgradeLevel: previewLevel ?? ownership.upgradeLevel, customization } : ownership}
          missing={!ownership}
          wished={detail.isWished}
          size="lg"
          eager
        />
        {unlockedLevel !== null && <span key={unlockedLevel} className="card-detail__upgrade-burst" aria-hidden="true" />}
        {ownership && (
          <div className="card-detail__preview-status">
            <span>{previewLevel !== null ? t('workshop.previewLevel', { level: previewLevel }) : t('detail.levelOf', { level: ownership.upgradeLevel, max: MAX_CARD_LEVEL })}</span>
            {previewLevel !== null && <button type="button" className="button button--small" onClick={() => setPreviewLevel(null)}>{t('workshop.backToCard')}</button>}
            {draftCustomization && <span>{t('workshop.unsaved')}</span>}
          </div>
        )}
      </div>

      <div className="card-detail__info">
        <RarityBadge rarity={character.rarity} />
        <h2 className="card-detail__name">{character.name}</h2>
        {character.nameNative && (
          <p className="card-detail__native" lang="ja">
            {character.nameNative}
          </p>
        )}
        {character.anime && (
          <Link to={`/animes/${character.anime.id}`} className="card-detail__anime">
            <RankBadge rank={character.anime.rank} size="sm" />
            <span>{character.anime.title}</span>
          </Link>
        )}
        {ownership ? (
          <CardWorkshop
            character={character}
            ownership={ownership}
            customization={customization}
            previewLevel={previewLevel}
            onPreview={setPreviewLevel}
            onChange={setDraftCustomization}
            tab={workshopTab}
            onTabChange={setWorkshopTab}
          />
        ) : (
          <p className="card-detail__missing">{t('detail.missing', { rarity: rarityLabel(character.rarity) })}</p>
        )}
        <details className="card-detail__facts">
          <summary>{t('workshop.cardInfo')}</summary>
          <dl className="facts">
            <div>
              <dt>{t('detail.favourites')}</dt>
              <dd>{formatNumber(character.favourites)}</dd>
            </div>
            <div>
              <dt>{t('detail.referenceValue')}</dt>
              <dd>
                <AnimaAmount
                  value={ownership ? effectiveCardValue(character.value, ownership.upgradeLevel) : character.value}
                  size={16}
                />
              </dd>
            </div>
            {character.combat && (
              <div>
                <dt>{t('detail.attack')}</dt>
                <dd className="facts__attack">{scaleCombatStats(character.combat, ownership?.upgradeLevel ?? 1).attack}</dd>
              </div>
            )}
            {character.combat && (
              <div>
                <dt>{t('detail.defense')}</dt>
                <dd className="facts__defense">{scaleCombatStats(character.combat, ownership?.upgradeLevel ?? 1).defense}</dd>
              </div>
            )}
            {role && (
              <div>
                <dt>{t('detail.role')}</dt>
                <dd>{role}</dd>
              </div>
            )}
            {ownership && (
              <div>
                <dt>{t('detail.obtainedOn')}</dt>
                <dd>{formatDate(ownership.firstObtainedAt)}</dd>
              </div>
            )}
            {ownership && (
              <div>
                <dt>{t('detail.copies')}</dt>
                <dd>{ownership.copies}</dd>
              </div>
            )}
            {ownership?.lock && (
              <div>
                <dt>{t('detail.lock')}</dt>
                <dd>
                  {ownership.lock.kind === 'listing'
                    ? t('detail.locks.listing')
                    : ownership.lock.kind === 'auction'
                      ? t('detail.locks.auction')
                      : t('detail.locks.trade')}
                </dd>
              </div>
            )}
            {detail.stashedFragments > 0 && !ownership && (
              <div>
                <dt>{t('detail.stashed')}</dt>
                <dd>{detail.stashedFragments}</dd>
              </div>
            )}
          </dl>
        </details>
        {ownership && <CardNametagEditor key={character.id} characterId={character.id} nametag={ownership.nametag} />}

        {ownership && (
          <div className="card-detail__actions">
            <button
              type="button"
              className="button"
              aria-pressed={detail.isWished}
              disabled={wish.isPending}
              onClick={() =>
                wish.mutate(
                  { characterId: character.id, wished: !detail.isWished },
                  {
                    onError: (error) =>
                      toasts.push({ tone: 'error', title: t('detail.wishFailed'), description: errorMessage(error) }),
                  },
                )
              }
            >
              <HeartIcon size={18} filled={detail.isWished} />
              {detail.isWished ? t('detail.removeWish') : t('detail.addWish')}
            </button>
            <button
              type="button"
              className="button"
              aria-pressed={detail.isFavorite}
              disabled={favorite.isPending}
              onClick={() =>
                favorite.mutate(
                  { characterId: character.id, favorite: !detail.isFavorite },
                  {
                    onError: (error) =>
                      toasts.push({ tone: 'error', title: t('detail.favoriteFailed'), description: errorMessage(error) }),
                  },
                )
              }
            >
              <StarIcon size={18} filled={detail.isFavorite} />
              {detail.isFavorite ? t('detail.removeFavorite') : t('detail.addFavorite')}
            </button>
            <button
              type="button"
              className="button"
              disabled={avatar.isPending}
              onClick={() =>
                avatar.mutate(character.id, {
                  onSuccess: () => toasts.push({ tone: 'success', title: t('detail.avatarUpdated'), description: character.name }),
                })
              }
            >
              <ProfileIcon size={18} />
              {t('detail.useAvatar')}
            </button>
            <button type="button" className="button" onClick={() => setShareOpen(true)}>
              <ShareIcon size={18} />
              {t('detail.share')}
            </button>
            <button
              type="button"
              className="button"
              onClick={() => {
                const url = `${window.location.origin}/personnages/${character.id}`;
                void navigator.clipboard?.writeText(url).then(() =>
                  toasts.push({ tone: 'success', title: t('detail.linkCopied'), description: url }),
                );
              }}
            >
              {t('detail.copyLink')}
            </button>
            <button type="button" className="button" disabled={Boolean(ownership.lock)} onClick={() => setSellOpen(true)}>
              <MarketIcon size={18} />
              {t('detail.sell')}
            </button>
          </div>
        )}
        {!ownership && (
          <>
            <button
              type="button"
              className="button"
              aria-pressed={detail.isWished}
              disabled={wish.isPending}
              onClick={() =>
                wish.mutate(
                  { characterId: character.id, wished: !detail.isWished },
                  {
                    onError: (error) =>
                      toasts.push({ tone: 'error', title: t('detail.wishFailed'), description: errorMessage(error) }),
                  },
                )
              }
            >
              <HeartIcon size={18} filled={detail.isWished} />
              {detail.isWished ? t('detail.removeWish') : t('detail.addWish')}
            </button>
            <p className="hint">{t('detail.wishHint')}</p>
          </>
        )}
      </div>

      <ShareCardSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        characterId={character.id}
        characterName={character.name}
      />
      {ownership && (
        <SellSheet open={sellOpen} onClose={() => setSellOpen(false)} character={character} ownership={ownership} />
      )}

      {ownership && step && (
        <div className="card-detail__cta">
          <button
            type="button"
            className="button button--primary button--large button--block"
            disabled={ownership.fragments < step.cost || Boolean(ownership.lock) || upgrade.isPending}
            onClick={runUpgrade}
          >
            <span>{upgrade.isPending ? t('fragments.upgrading') : t('detail.upgradeTo', { level: step.toLevel })}</span>
            <small>{t('workshop.costShort', { cost: formatNumber(step.cost) })}</small>
          </button>
        </div>
      )}
    </div>
  );
}
